import { UAParser } from 'ua-parser-js';
import { ExtraDevices } from 'ua-parser-js/extensions';
import * as androidDevices from 'android-device-list';

export interface DeviceInfo {
  deviceType: string;
  os: string;
  userAgent: string;
  browser?: string;
  browserVersion?: string;
}

/**
 * Get a friendly device name based on vendor, model, and OS
 */
function getFriendlyDeviceName(
  deviceVendor: string,
  deviceModel: string,
  deviceType: string,
  osName: string
): string {
  // Handle Apple devices (iOS, iPadOS, macOS)
  if (deviceVendor === 'Apple' || osName === 'iOS' || osName === 'Mac OS') {
    if (deviceModel) {
      // iOS devices often have friendly names in the model
      if (deviceModel.toLowerCase().includes('iphone')) {
        return deviceModel; // e.g., "iPhone"
      } else if (deviceModel.toLowerCase().includes('ipad')) {
        return deviceModel; // e.g., "iPad"
      } else if (deviceModel.toLowerCase().includes('ipod')) {
        return deviceModel; // e.g., "iPod"
      } else if (deviceModel.toLowerCase().includes('mac')) {
        return deviceModel; // e.g., "Macintosh"
      }
    }
    // Fallback for Apple devices
    if (osName === 'iOS') {
      return deviceType === 'tablet' ? 'iPad' : 'iPhone';
    } else if (osName === 'Mac OS') {
      return 'Mac';
    }
  }

  // Handle Android devices with database lookup
  if (osName === 'Android' && deviceModel) {
    const deviceMatches = androidDevices.getDevicesByModel(deviceModel);
    if (deviceMatches && deviceMatches.length > 0) {
      const marketingName = deviceMatches[0].name;
      console.log('[DeviceInfo] ✅ Found Android marketing name:', marketingName);
      return deviceVendor ? `${deviceVendor} ${marketingName}`.trim() : marketingName;
    }
  }

  // Handle other vendors with known model codes
  if (deviceVendor && deviceModel) {
    // For vendors like Samsung, Google, etc., show vendor + model
    return `${deviceVendor} ${deviceModel}`.trim();
  } else if (deviceModel) {
    return deviceModel;
  }

  // Fallback based on device type
  if (deviceType === 'mobile') {
    return 'Mobile Phone';
  } else if (deviceType === 'tablet') {
    return 'Tablet';
  } else if (deviceType === 'desktop') {
    return 'Desktop';
  }

  return 'Unknown Device';
}

/**
 * Get detailed device information from browser APIs using Client Hints
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const ua = navigator.userAgent;
  const parser = new UAParser(ua, ExtraDevices);
  
  console.log('[DeviceInfo] Starting device detection...');
  console.log('[DeviceInfo] User Agent:', ua);
  console.log('[DeviceInfo] navigator.userAgentData available:', !!(navigator as unknown as { userAgentData?: unknown }).userAgentData);
  
  // Try to get enhanced info with Client Hints
  try {
    const result = await parser.getResult().withClientHints();
    
    console.log('[DeviceInfo] ✅ Successfully got Client Hints!');
    console.log('[DeviceInfo] Device:', result.device);
    console.log('[DeviceInfo] OS:', result.os);
    
    // Get device info
    const device = result.device;
    const deviceVendor = device.vendor || '';
    const deviceModel = device.model || '';
    const deviceType = device.type || 'desktop';
    
    // Get OS info first (needed for device name resolution)
    const os = result.os;
    const osName = os.name || 'Unknown';
    
    // Get friendly device name
    const fullDeviceName = getFriendlyDeviceName(deviceVendor, deviceModel, deviceType, osName);
    console.log('[DeviceInfo] Final device name:', fullDeviceName);
    const osVersion = os.version || '';
    const fullOsName = osVersion ? `${osName} ${osVersion}` : osName;
    
    // Get browser info
    const browser = result.browser;
    const browserName = browser.name || 'Unknown';
    const browserVersion = browser.version?.split('.')[0] || '';
    const fullBrowserName = browserVersion ? `${browserName} ${browserVersion}` : browserName;

    return {
      deviceType: fullDeviceName,
      os: fullOsName,
      userAgent: ua,
      browser: fullBrowserName,
      browserVersion,
    };
  } catch (error) {
    console.warn('[DeviceInfo] Client Hints not available, falling back to user agent only:', error);
    
    // Fallback to regular user agent parsing
    const result = parser.getResult();
    
    const device = result.device;
    const deviceVendor = device.vendor || '';
    const deviceModel = device.model || '';
    const deviceType = device.type || 'desktop';
    
    // Get OS info first (needed for device name resolution)
    const os = result.os;
    const osName = os.name || 'Unknown';
    
    // Get friendly device name using the same helper
    const fullDeviceName = getFriendlyDeviceName(deviceVendor, deviceModel, deviceType, osName);
    console.log('[DeviceInfo] Final device name (fallback):', fullDeviceName);
    
    const osVersion = os.version || '';
    const fullOsName = osVersion ? `${osName} ${osVersion}` : osName;
    
    const browser = result.browser;
    const browserName = browser.name || 'Unknown';
    const browserVersion = browser.version?.split('.')[0] || '';
    const fullBrowserName = browserVersion ? `${browserName} ${browserVersion}` : browserName;

    return {
      deviceType: fullDeviceName,
      os: fullOsName,
      userAgent: ua,
      browser: fullBrowserName,
      browserVersion,
    };
  }
}
